import React, { useRef, useState } from "react";
import { Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

interface PdfFileDropzoneProps {
  file: File | null;
  onFile: (f: File | null) => void;
}

/**
 * Click-or-drag PDF picker. Shared by the Forms PDF-upload dialog and the chat
 * "Send a form" dialog.
 *
 * The drag handlers call `stopPropagation()` so a drop is never re-handled by an
 * ancestor drop zone — notably the chat composer, whose `SendFormDialog` is a
 * React child even though Radix portals it out of the DOM, so React synthetic
 * drop events would otherwise bubble into the composer and attach the PDF to the
 * chat instead of this picker.
 */
export function PdfFileDropzone({ file, onFile }: PdfFileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.type === "application/pdf") {
      onFile(dropped);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) {
      onFile(picked);
    }
    e.target.value = "";
  };

  if (file) {
    return (
      <div className="flex min-w-0 items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
          <FileText size={18} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 [overflow-wrap:anywhere]">
            {file.name}
          </p>
          <p className="text-xs text-slate-500">
            {(file.size / 1024).toFixed(0)} KB
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onFile(null)}
          className="h-12 w-12 shrink-0 text-slate-500 hover:bg-red-50 hover:text-red-600"
          aria-label="Remove file"
        >
          <X size={16} />
        </Button>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors focus-visible:outline-hidden focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/10 ${
        isDragging
          ? "border-primary bg-primary/5"
          : "border-slate-300 bg-slate-50 hover:border-primary/50 hover:bg-primary/5"
      }`}
    >
      <Upload size={24} className="text-primary" />
      <p className="text-sm text-slate-600">
        Drop a PDF here or{" "}
        <span className="text-primary font-semibold">browse</span>
      </p>
      <p className="text-xs text-slate-500">PDF only</p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
